// ./src/components/features/AccountEmails/AccountEmailsTable.tsx
import React, { useMemo } from "react";
import { Text, Group } from "@mantine/core";

import {
  AppTable,
  SortConfig,
  AppTableColumn,
  RecordActions,
  AppButton,
  StatusBadge,
} from "@/components/generic";
import { AccountEmail } from "@/utils/api";
import { formatDateTime } from "@/utils/dateHelpers";
import { OPERATION_RIGHTS } from "@/config";

export interface AccountEmailsTableProps {
  /** Array of account emails to display */
  accountEmails: AccountEmail[];

  /** Loading state for initial load */
  loading: boolean;

  /** Loading state for loading more items */
  loadingMore: boolean;

  /** Whether there are more items to load */
  hasMore: boolean;

  /** Current sort configuration */
  sortBy: SortConfig[];

  /** Callback when sort changes */
  onSortChange: (sortBy: SortConfig[]) => void;

  /** Callback to load more items */
  onLoadMore: () => void;

  /** Callback when edit action is clicked */
  onEditAccountEmail?: (accountEmail: AccountEmail) => void;

  /** Callback when confirm action is clicked */
  onConfirmAccountEmail?: (accountEmail: AccountEmail) => void;

  /** Callback when remove action is clicked */
  onRemoveAccountEmail?: (accountEmail: AccountEmail) => void;

  /** ID of account email currently being removed */
  removingAccountEmailId?: string | null;

  /** ID of account email just edited or added */
  editedItemId?: string | null | undefined;

  /** Table size */
  size?: "xs" | "sm" | "md" | "lg" | "xl";
}

export const AccountEmailsTable: React.FC<AccountEmailsTableProps> = ({
  accountEmails,
  loading,
  loadingMore,
  hasMore,
  sortBy,
  onSortChange,
  onLoadMore,
  onEditAccountEmail,
  onConfirmAccountEmail,
  onRemoveAccountEmail,
  removingAccountEmailId,
  editedItemId,
  size = "md",
}) => {
  const columns: AppTableColumn<AccountEmail>[] = useMemo(
    () => [
      {
        key: "email",
        label: "Email & Description",
        sortable: true,
        flex: true,
        minWidth: 300,
        render: (value, record) => (
          <div>
            <Text fw={500}>{value || "No email"}</Text>
            {record.description && (
              <Text size="xs" c="dimmed" lineClamp={2}>
                {record.description}
              </Text>
            )}
          </div>
        ),
      },
      {
        key: "confirmedAt",
        label: "Confirmed At",
        sortable: true,
        width: 180,
        minWidth: 150,
        align: "left",
        render: (value) => {
          if (!value) {
            return (
              <Text size="sm" c="dimmed">
                —
              </Text>
            );
          }
          return (
            <Text
              size="sm"
              dangerouslySetInnerHTML={{
                __html: formatDateTime(value, "datetime2"),
              }}
            ></Text>
          );
        },
      },
      {
        key: "status",
        label: "Status",
        sortable: true,
        width: 120,
        minWidth: 100,
        align: "left",
        render: (value) => {
          return <StatusBadge status={value} />;
        },
      },
      {
        key: "createdAt",
        label: "Created",
        sortable: true,
        width: 150,
        minWidth: 150,
        align: "left",
        render: (value) => (
          <Text
            size="sm"
            c="dimmed"
            dangerouslySetInnerHTML={{
              __html: formatDateTime(value, "datetime2"),
            }}
          ></Text>
        ),
      },
      {
        key: "actions",
        label: "",
        width: 220,
        minWidth: 220,
        align: "right",
        render: (_, record: AccountEmail) => {
          const isConfirmed = !!record.confirmedAt;
          const isRemoving = removingAccountEmailId === record.id;
          const canEdit = record.rights?.includes(OPERATION_RIGHTS.UPDATE);

          return (
            <Group gap="xs" wrap="nowrap" justify={"flex-end"}>
              {!isConfirmed && canEdit && onConfirmAccountEmail && (
                <AppButton
                  variant="secondary"
                  size="xs"
                  iconName="Mail"
                  onClick={() => onConfirmAccountEmail(record)}
                  disabled={isRemoving}
                >
                  Confirm
                </AppButton>
              )}
              <RecordActions
                context={record}
                // Edit only allowed for unconfirmed emails
                onEditClick={onEditAccountEmail}
                onRemoveClick={onRemoveAccountEmail}
                removeInProgress={isRemoving}
              />
            </Group>
          );
        },
      },
    ],
    [
      onEditAccountEmail,
      onConfirmAccountEmail,
      onRemoveAccountEmail,
      removingAccountEmailId,
    ]
  );

  return (
    <AppTable
      data={accountEmails}
      hideOnNoData={true}
      columns={columns}
      loading={loading}
      hasMore={hasMore}
      loadingMore={loadingMore}
      onLoadMore={onLoadMore}
      sortBy={sortBy}
      onSortChange={onSortChange}
      multiSort={false}
      getRowKey={(accountEmail) => accountEmail?.id || ""}
      highlightRow={(accountEmail) => accountEmail?.id === editedItemId}
      withBorder
      highlightOnHover
      size={size}
    />
  );
};
